import * as React from 'react';

import type { CarouselApi } from '@/components/ui/carousel';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { Button } from '@/components/ui/button';

export type PageControllerPage = {
  key: string;
  label: string;
  content: React.ReactNode;
};

export function PageController({
  pages,
  storageKey,
  initialIndex = 0,
  dockIndicators = false,
}: {
  pages: PageControllerPage[];
  storageKey?: string;
  initialIndex?: number;
  dockIndicators?: boolean;
}) {
  const safeInitialIndex = Math.min(Math.max(initialIndex, 0), Math.max(0, pages.length - 1));

  const [api, setApi] = React.useState<CarouselApi | null>(null);
  const [index, setIndex] = React.useState(() => {
    if (!storageKey || typeof window === 'undefined') return safeInitialIndex;
    try {
      const raw = window.localStorage.getItem(storageKey);
      const next = raw ? Number(raw) : safeInitialIndex;
      if (!Number.isFinite(next)) return safeInitialIndex;
      return Math.min(Math.max(next, 0), Math.max(0, pages.length - 1));
    } catch {
      return safeInitialIndex;
    }
  });

  React.useEffect(() => {
    if (!api) return;

    const handleSelect = () => {
      const selected = api.selectedScrollSnap();
      setIndex(selected);
      if (!storageKey) return;
      try {
        window.localStorage.setItem(storageKey, String(selected));
      } catch {
        // ignore
      }
    };

    handleSelect();
    api.on('select', handleSelect);
    api.on('reInit', handleSelect);

    return () => {
      api.off('select', handleSelect);
      api.off('reInit', handleSelect);
    };
  }, [api, storageKey]);

  React.useEffect(() => {
    if (!api) return;
    api.scrollTo(index);
  }, [api, index]);

  if (pages.length <= 1) {
    return <>{pages[0]?.content ?? null}</>;
  }

  const indicators = (
    <div className="flex items-center justify-center gap-2" aria-label="Page indicators">
      {pages.map((page, pageIndex) => {
        const selected = pageIndex === index;
        return (
          <Button
            key={page.key}
            type="button"
            variant="ghost"
            size="sm"
            className={
              selected
                ? 'h-8 w-8 rounded-full bg-muted/50 text-foreground'
                : 'h-8 w-8 rounded-full text-muted-foreground'
            }
            aria-current={selected ? 'page' : undefined}
            aria-label={page.label}
            onClick={() => api?.scrollTo(pageIndex)}
          >
            <span className={selected ? 'text-[10px] font-semibold' : 'text-[10px]'}>{pageIndex + 1}</span>
          </Button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-3">
      <Carousel setApi={setApi} opts={{ loop: false, align: 'start' }}>
        <CarouselContent>
          {pages.map((page) => (
            <CarouselItem key={page.key} className="pl-0">
              {page.content}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {dockIndicators ? (
        <div
          className="fixed inset-x-0 z-40 flex justify-center"
          style={{
            bottom: 'calc(3rem + var(--safe-area-bottom, 0px))',
          }}
        >
          <div className="mx-auto max-w-screen-md px-3 pb-2">
            <div className="rounded-full bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70 px-2 py-1">
              {indicators}
            </div>
          </div>
        </div>
      ) : (
        indicators
      )}
    </div>
  );
}
